import {t, Trans} from '@lingui/macro';
import React, {FC, useEffect, useMemo} from 'react';

import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {getParameters} from '../../../editors/expressions/expressionInferHelpers';
import {isListType, ResultTypesEnum} from '../../../editors/expressions/infer/internal';
import {JoinSeparatorsEnum} from '../../../editors/expressions/infer/listType';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {AtomCardProps} from './atomCard';
import {AtomIcon} from './atomIcon';
import {AtomTitle} from './atomTitle';
import {AtomParameterPicker} from './pickers/atomParameterPicker';

/*
 * Component.
 */

const listStringError = t('expression-join-error-liststring')`You can only join a Text list.`;

export const JoinAtomCard: FC<AtomCardProps> = (props) => {
  const {atom, isDisabled, onUpdateAtom, onCancel} = props;
  const [separatorParam, customJoinParam] = getParameters(atom);

  const localize = useLocalize();
  const stringListErrorMessage = useMemo(() => localize(listStringError), [localize]);

  const shouldDisplayCustomSeparator = atom.params.separator === JoinSeparatorsEnum.CUSTOM_JOIN;

  useEffect(() => {
    const {previousInfer} = atom;
    const isStringList = isListType(previousInfer) && previousInfer.itemType.type === ResultTypesEnum.STRING;

    if (isStringList && atom.errorMessage === stringListErrorMessage) {
      onUpdateAtom({...atom, errorMessage: undefined});
    } else if (!isStringList && atom.errorMessage !== stringListErrorMessage) {
      onUpdateAtom({...atom, errorMessage: stringListErrorMessage});
    }
  }, [atom, onUpdateAtom, stringListErrorMessage]);

  const title = (
    <AtomTitle atom={atom} title={<Trans id="expression-join-title">Join with</Trans>}>
      <AtomParameterPicker
        parameter={separatorParam}
        atom={atom}
        isDisabled={isDisabled}
        onUpdate={onUpdateAtom}
      />
    </AtomTitle>
  );

  return (
    <RuleMultilineParameterCard
      errorMessage={atom.errorMessage}
      icon={<AtomIcon atom={atom} />}
      title={title}
      onCancel={onCancel}
    >
      {shouldDisplayCustomSeparator && (
        <AtomParameterPicker
          parameter={customJoinParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      )}
    </RuleMultilineParameterCard>
  );
};
